import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;
const IDLE_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes of inactivity

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('aegis_token'));
  const [loading, setLoading] = useState(true);
  const idleTimer = useRef(null);

  const logout = useCallback(() => {
    localStorage.removeItem('aegis_token');
    localStorage.removeItem('aegis_last_active');
    setToken(null);
    setUser(null);
    if (idleTimer.current) clearTimeout(idleTimer.current);
  }, []);

  // Reset idle timer on any user activity
  const resetIdleTimer = useCallback(() => {
    if (!token) return;
    localStorage.setItem('aegis_last_active', String(Date.now()));
    if (idleTimer.current) clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => {
      logout();
    }, IDLE_TIMEOUT_MS);
  }, [token, logout]);

  // Check if session expired while app was closed/backgrounded
  useEffect(() => {
    if (!token) return;
    const lastActive = localStorage.getItem('aegis_last_active');
    if (lastActive) {
      const elapsed = Date.now() - parseInt(lastActive, 10);
      if (elapsed > IDLE_TIMEOUT_MS) {
        logout();
        return;
      }
    }
  }, [token, logout]);

  // Listen for user activity events
  useEffect(() => {
    if (!token) return;

    const events = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach(e => window.addEventListener(e, resetIdleTimer, { passive: true }));
    resetIdleTimer(); // Start the timer

    // Check tab visibility — logout if idle when returning
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        const lastActive = localStorage.getItem('aegis_last_active');
        if (lastActive) {
          const elapsed = Date.now() - parseInt(lastActive, 10);
          if (elapsed > IDLE_TIMEOUT_MS) {
            logout();
            return;
          }
        }
        resetIdleTimer();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      events.forEach(e => window.removeEventListener(e, resetIdleTimer));
      document.removeEventListener('visibilitychange', handleVisibility);
      if (idleTimer.current) clearTimeout(idleTimer.current);
    };
  }, [token, resetIdleTimer, logout]);

  useEffect(() => {
    const initAuth = async () => {
      const savedToken = localStorage.getItem('aegis_token');
      if (savedToken) {
        try {
          const response = await axios.get(`${API_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${savedToken}` }
          });
          setUser(response.data);
          setToken(savedToken);
          localStorage.setItem('aegis_last_active', String(Date.now()));
        } catch {
          localStorage.removeItem('aegis_token');
          localStorage.removeItem('aegis_last_active');
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('aegis_token', access_token);
    localStorage.setItem('aegis_last_active', String(Date.now()));
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, full_name, phone) => {
    const response = await axios.post(`${API_URL}/api/auth/register`, { email, password, full_name, phone });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('aegis_token', access_token);
    localStorage.setItem('aegis_last_active', String(Date.now()));
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${token}`
  });

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, getAuthHeaders, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
